import type { ValidationIssue } from "@zworks/console-contracts";
import { ArrowLeft, CheckCircle2, PanelRight, Plus, Save, ShieldCheck, Zap } from "lucide-react";
import * as React from "react";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export type ViewMode = "canvas" | "split" | "outline";

export function FlowToolbar({
  canEdit,
  busy,
  issues,
  inspectorOpen,
  hasSelection,
  extraActions,
  onToggleInspector,
  onAddStep,
  onValidate,
  onSave,
  onActivate,
}: {
  canEdit: boolean;
  busy: boolean;
  issues: ValidationIssue[] | null;
  inspectorOpen?: boolean;
  hasSelection?: boolean;
  extraActions?: React.ReactNode;
  onToggleInspector?: () => void;
  onAddStep: () => void;
  onValidate: () => void;
  onSave: () => void;
  onActivate: () => void;
}) {
  const issueCount = issues?.length ?? 0;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-card/75 px-3 py-2 backdrop-blur-md rounded-xl border border-border/70 shadow-sm shrink-0">
      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant="ghost" size="sm" className="h-8 gap-1.5 px-2.5">
          <Link to="/studio/workflows">
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            <span>Back to workflows</span>
          </Link>
        </Button>

        {hasSelection && onToggleInspector ? (
          <Button
            type="button"
            variant={inspectorOpen ? "secondary" : "ghost"}
            size="sm"
            className="h-8 px-2.5 text-xs gap-1.5 border border-border/50"
            onClick={onToggleInspector}
            title={inspectorOpen ? "Hide step detail" : "Show step detail"}
            aria-label={inspectorOpen ? "Hide step detail" : "Show step detail"}
          >
            <PanelRight className="size-3.5" aria-hidden="true" />
            <span>Step Detail</span>
          </Button>
        ) : null}

        {!canEdit ? (
          <Badge variant="outline" className="text-xs">
            Read only
          </Badge>
        ) : null}

        {issueCount > 0 ? (
          <Badge variant="danger" className="text-xs gap-1">
            <span>{issueCount} issue(s)</span>
          </Badge>
        ) : issues !== null ? (
          <Badge variant="success" className="text-xs gap-1">
            <CheckCircle2 className="size-3" aria-hidden="true" />
            <span>Valid</span>
          </Badge>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* Primary Action Buttons */}
        {extraActions}
        {canEdit ? (
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={onAddStep}
            className="h-8 gap-1.5 text-xs"
          >
            <Plus className="size-3.5" aria-hidden="true" />
            <span>Add step</span>
          </Button>
        ) : null}

        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={onValidate}
          className="h-8 gap-1.5 text-xs"
        >
          <ShieldCheck className="size-3.5" aria-hidden="true" />
          <span>Validate</span>
        </Button>

        {canEdit ? (
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={onSave}
              className="h-8 gap-1.5 text-xs"
            >
              <Save className="size-3.5" aria-hidden="true" />
              <span>Save draft</span>
            </Button>

            <Button size="sm" disabled={busy} onClick={onActivate} className="h-8 gap-1.5 text-xs">
              <Zap className="size-3.5" aria-hidden="true" />
              <span>Activate</span>
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}
